import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { trainingAPI, certificateSettingsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Search,
  AlertCircle,
  Eye,
  CheckCircle2,
  Download,
  RefreshCw,
  FileDown,
  CheckSquare,
  Square,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  background: ${props => props.theme.colors.white};
  min-width: 150px;
`;

const RegistrationTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem;
    border-bottom: 1px solid ${props => props.theme.colors.border.light};
    text-align: left;
  }

  th {
    font-size: ${props => props.theme.typography.fontSize.sm};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => props.theme.colors.text.secondary};
    position: sticky;
    top: 0;
    background: ${props => props.theme.colors.background.primary};
    z-index: 1;
  }

  td {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.primary};
  }

  tbody tr:hover {
    background-color: ${props => props.theme.colors.background.secondary};
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.background.secondary};
  }
  
  svg {
    margin-left: 0.25rem;
    opacity: ${props => props.active ? 1 : 0.3};
  }
`;

const RegistrationFilters = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.border.light};
`;

const PaginationInfo = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: ${props => props.theme.colors.primary}15;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const CheckboxCell = styled.td`
  width: 40px;
  padding: 0.5rem !important;
  
  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
`;

const StatusDot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: capitalize;
  color: ${props => {
    switch (props.status) {
      case 'approved':
      case 'completed':
      case 'attended':
        return props.theme.colors.success;
      case 'declined':
        return props.theme.colors.error;
      case 'waitlisted':
        return props.theme.colors.warning;
      default:
        return props.theme.colors.info;
    }
  }};
`;

const FormDetailsWrapper = styled.div`
  margin-top: 0.75rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FormDetailsTitle = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem 1.25rem;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FormFieldLabel = styled.span`
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const FormFieldValue = styled.span`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  word-break: break-word;
  white-space: pre-wrap;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DialogMessage = styled.p`
  margin: 0;
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.6;
  font-size: ${p => p.theme.typography.fontSize.base};
`;

const DialogActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-family: ${p => p.theme.typography.fontFamily.primary};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.muted};
  }
`;

const TrainingRegistrations = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const [training, setTraining] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationSummary, setRegistrationSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationSearch, setRegistrationSearch] = useState('');
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState('');
  const [registrationSortBy, setRegistrationSortBy] = useState('created_at');
  const [registrationSortOrder, setRegistrationSortOrder] = useState('desc');
  const [registrationPage, setRegistrationPage] = useState(1);
  const [registrationPageSize] = useState(25);
  const [selectedRegistrations, setSelectedRegistrations] = useState(new Set());
  const [registrationDetail, setRegistrationDetail] = useState(null);
  const [showRegistrationDetailModal, setShowRegistrationDetailModal] = useState(false);
  
  // Custom dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Confirm',
    variant: 'primary',
  });
  const [declineDialog, setDeclineDialog] = useState({
    isOpen: false,
    registration: null,
    reason: '',
  });
  const [bulkActionDialog, setBulkActionDialog] = useState({
    isOpen: false,
    status: '',
    count: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [registrationsResponse, trainingResponse] = await Promise.all([
          trainingAPI.getRegistrations(trainingId),
          trainingAPI.getById(trainingId),
        ]);

        setRegistrations(registrationsResponse.data?.data || []);
        setRegistrationSummary(registrationsResponse.data?.summary || null);
        setTraining(trainingResponse.data || null);
      } catch (error) {
        console.error('Failed to load registrations:', error);
        toast.error('Unable to load registrations.');
        navigate('/app/training/manage');
      } finally {
        setLoading(false);
      }
    };

    if (trainingId) {
      loadData();
    }
  }, [trainingId, navigate]);

  const getRemainingSeats = (training) => {
    if (!training || !training.max_participants) return null;
    const reserved = registrationSummary?.total || 0;
    return Math.max(training.max_participants - reserved, 0);
  };

  // Filtered and sorted registrations
  const filteredRegistrations = useMemo(() => {
    let filtered = [...registrations];

    // Search filter
    if (registrationSearch) {
      const searchLower = registrationSearch.toLowerCase();
      filtered = filtered.filter(reg =>
        reg.full_name?.toLowerCase().includes(searchLower) ||
        reg.email?.toLowerCase().includes(searchLower) ||
        reg.organization?.toLowerCase().includes(searchLower) ||
        reg.phone?.toLowerCase().includes(searchLower) ||
        reg.membership_id?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (registrationStatusFilter) {
      filtered = filtered.filter(reg => reg.status === registrationStatusFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[registrationSortBy];
      let bVal = b[registrationSortBy];

      if (registrationSortBy === 'created_at' || registrationSortBy === 'approved_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (registrationSortOrder === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });

    return filtered;
  }, [registrations, registrationSearch, registrationStatusFilter, registrationSortBy, registrationSortOrder]);

  // Paginated registrations
  const paginatedRegistrations = useMemo(() => {
    const start = (registrationPage - 1) * registrationPageSize;
    const end = start + registrationPageSize;
    return filteredRegistrations.slice(start, end);
  }, [filteredRegistrations, registrationPage, registrationPageSize]);

  const totalPages = Math.ceil(filteredRegistrations.length / registrationPageSize);

  const handleSort = (column) => {
    if (registrationSortBy === column) {
      setRegistrationSortOrder(registrationSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setRegistrationSortBy(column);
      setRegistrationSortOrder('desc');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRegistrations(new Set(paginatedRegistrations.map(r => r.id)));
    } else {
      setSelectedRegistrations(new Set());
    }
  };

  const handleSelectRegistration = (id, checked) => {
    const newSet = new Set(selectedRegistrations);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedRegistrations(newSet);
  };

  const handleRegistrationStatus = async (registration, status) => {
    if (status === 'declined') {
      setDeclineDialog({
        isOpen: true,
        registration,
        reason: '',
      });
      return;
    }
    
    if (status === 'completed') {
      setConfirmDialog({
        isOpen: true,
        title: 'Complete Training',
        message: `Mark ${registration.full_name} as completed and issue their certificate?`,
        confirmText: 'Complete & Issue Certificate',
        variant: 'primary',
        onConfirm: async () => {
          await updateRegistrationStatus(registration, status, null);
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        },
      });
      return;
    }
    
    // For other statuses, update directly
    await updateRegistrationStatus(registration, status, null);
  };

  const updateRegistrationStatus = async (registration, status, rejectionReason) => {
    try {
      await trainingAPI.updateRegistrationStatus(registration.id, {
        status,
        rejection_reason: rejectionReason,
      });
      toast.success(`Registration ${status}.`);
      // Reload data
      const [registrationsResponse] = await Promise.all([
        trainingAPI.getRegistrations(trainingId),
      ]);
      setRegistrations(registrationsResponse.data?.data || []);
      setRegistrationSummary(registrationsResponse.data?.summary || null);
      if (registrationDetail?.id === registration.id) {
        setRegistrationDetail((prev) =>
          prev
            ? {
                ...prev,
                status,
                rejection_reason: rejectionReason,
              }
            : prev
        );
      }
    } catch (error) {
      console.error('Failed to update registration:', error);
      const message = error.response?.data?.message || 'Unable to update registration.';
      toast.error(message);
    }
  };

  const handleDeclineConfirm = async () => {
    const { registration, reason } = declineDialog;
    await updateRegistrationStatus(registration, 'declined', reason);
    setDeclineDialog({ isOpen: false, registration: null, reason: '' });
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRegistrations.size === 0) {
      toast.error('Please select at least one registration.');
      return;
    }

    setBulkActionDialog({
      isOpen: true,
      status,
      count: selectedRegistrations.size,
    });
  };

  const confirmBulkAction = async () => {
    const { status } = bulkActionDialog;
    const updates = Array.from(selectedRegistrations).map(id => {
      return trainingAPI.updateRegistrationStatus(id, { status });
    });

    try {
      await Promise.all(updates);
      toast.success(`${selectedRegistrations.size} registration(s) updated.`);
      setSelectedRegistrations(new Set());
      // Reload data
      const [registrationsResponse] = await Promise.all([
        trainingAPI.getRegistrations(trainingId),
      ]);
      setRegistrations(registrationsResponse.data?.data || []);
      setRegistrationSummary(registrationsResponse.data?.summary || null);
      setBulkActionDialog({ isOpen: false, status: '', count: 0 });
    } catch (error) {
      console.error('Failed to update registrations:', error);
      toast.error('Some registrations could not be updated.');
      setBulkActionDialog({ isOpen: false, status: '', count: 0 });
    }
  };


  const exportRegistrations = () => {
    const headers = ['Name', 'Email', 'Phone', 'Organization', 'Status', 'Registered On'];
    const rows = filteredRegistrations.map(reg => [
      reg.full_name || '',
      reg.email || '',
      reg.phone || '',
      reg.organization || '',
      reg.status || '',
      reg.created_at ? new Date(reg.created_at).toLocaleString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${training?.title || 'training'}_registrations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Registrations exported successfully.');
  };

  const openRegistrationDetail = (registration) => {
    setRegistrationDetail(registration);
    setShowRegistrationDetailModal(true);
  };

  const formatFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    }
    return value.toString();
  };

  const buildRegistrationResponses = (registration) => {
    const responses = [];
    if (registration.membership_id) {
      responses.push({
        label: 'Membership ID',
        value: registration.membership_id,
      });
    }

    if (registration.form_data && typeof registration.form_data === 'object') {
      Object.entries(registration.form_data).forEach(([key, value]) => {
        responses.push({
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: formatFieldValue(value),
        });
      });
    }

    [
      { key: 'email', label: 'Email' },
      { key: 'motivation', label: 'Motivation for Attending' },
      { key: 'special_needs', label: 'Special Requirements' },
      { key: 'region', label: 'Region' },
      { key: 'phone', label: 'Phone Number' },
      { key: 'organization', label: 'Organization' },
      { key: 'role_title', label: 'Role / Title' },
    ].forEach(({ key, label }) => {
      if (registration[key]) {
        responses.push({
          label,
          value: formatFieldValue(registration[key]),
        });
      }
    });

    return responses.filter((response) => response.value && response.value !== '—');
  };

  const registrationDetailResponses = useMemo(() => {
    if (!registrationDetail) return [];
    return buildRegistrationResponses(registrationDetail);
  }, [registrationDetail]);

  if (loading) {
    return (
      <PageContainer>
        <Card>
          <p>Loading registrations…</p>
        </Card>
      </PageContainer>
    );
  }

  if (!training) {
    return (
      <PageContainer>
        <Card>
          <p>Training not found.</p>
          <Button onClick={() => navigate('/app/training/manage')}>Back to Training Management</Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/training/manage')}
            leftIcon={<ArrowLeft size={16} />}
          >
            Back to Training Management
          </Button>
          <Title theme={theme} style={{ marginTop: '0.5rem' }}>
            {training.title} - Registrations
          </Title>
        </div>
      </Header>

      <Card>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <Badge theme={theme}>Total: {registrationSummary?.total ?? registrations.length}</Badge>
          <Badge theme={theme}>Pending: {registrationSummary?.pending ?? 0}</Badge>
          <Badge theme={theme}>Approved: {registrationSummary?.approved ?? 0}</Badge>
          <Badge theme={theme}>Declined: {registrationSummary?.declined ?? 0}</Badge>
          <Badge theme={theme}>Waitlisted: {registrationSummary?.waitlisted ?? 0}</Badge>
          {training?.max_participants && (
            <Badge
              theme={theme}
              style={{
                background: (training?.is_full ? theme.colors.error : theme.colors.primary) + '15',
                color: training?.is_full ? theme.colors.error : theme.colors.primary,
              }}
            >
              {training?.is_full
                ? `Capacity reached (${training.max_participants})`
                : `${getRemainingSeats(training)} of ${training.max_participants} seats left`}
            </Badge>
          )}
        </div>
        {training?.is_full && (
          <p style={{ color: theme.colors.error, marginTop: 0, marginBottom: '1rem' }}>
            This training has reached capacity. New registrations are blocked automatically.
          </p>
        )}

        <RegistrationFilters theme={theme}>
          <Input
            placeholder="Search by name, email, organization..."
            value={registrationSearch}
            onChange={(e) => {
              setRegistrationSearch(e.target.value);
              setRegistrationPage(1);
            }}
            leftIcon={<Search size={14} />}
            style={{ flex: 1, minWidth: '250px' }}
          />
          <Select
            theme={theme}
            value={registrationStatusFilter}
            onChange={(e) => {
              setRegistrationStatusFilter(e.target.value);
              setRegistrationPage(1);
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="waitlisted">Waitlisted</option>
            <option value="declined">Declined</option>
            <option value="attended">Attended</option>
            <option value="completed">Completed</option>
          </Select>
          <Button
            variant="outline"
            size="small"
            leftIcon={<FileDown size={14} />}
            onClick={exportRegistrations}
          >
            Export CSV
          </Button>
        </RegistrationFilters>

        {selectedRegistrations.size > 0 && (
          <BulkActionsBar theme={theme}>
            <span style={{ fontWeight: 600 }}>
              {selectedRegistrations.size} selected
            </span>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="primary"
                onClick={() => handleBulkStatusUpdate('approved')}
              >
                Approve Selected
              </Button>
              <Button
                size="small"
                variant="outline"
                onClick={() => handleBulkStatusUpdate('waitlisted')}
              >
                Waitlist Selected
              </Button>
              <Button
                size="small"
                variant="danger"
                onClick={() => handleBulkStatusUpdate('declined')}
              >
                Decline Selected
              </Button>
              <Button
                size="small"
                variant="ghost"
                onClick={() => setSelectedRegistrations(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          </BulkActionsBar>
        )}

        <div style={{ maxHeight: '60vh', overflowY: 'auto', border: `1px solid ${theme.colors.border.light}`, borderRadius: theme.borderRadius.md }}>
          <RegistrationTable theme={theme}>
            <thead>
              <tr>
                <CheckboxCell as="th" theme={theme} style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={paginatedRegistrations.length > 0 && paginatedRegistrations.every(r => selectedRegistrations.has(r.id))}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </CheckboxCell>
                <SortableHeader
                  theme={theme}
                  active={registrationSortBy === 'full_name'}
                  onClick={() => handleSort('full_name')}
                >
                  Applicant
                  <ArrowUpDown size={12} />
                </SortableHeader>
                <SortableHeader
                  theme={theme}
                  active={registrationSortBy === 'organization'}
                  onClick={() => handleSort('organization')}
                >
                  Organization
                  <ArrowUpDown size={12} />
                </SortableHeader>
                <SortableHeader
                  theme={theme}
                  active={registrationSortBy === 'status'}
                  onClick={() => handleSort('status')}
                >
                  Status
                  <ArrowUpDown size={12} />
                </SortableHeader>
                <SortableHeader
                  theme={theme}
                  active={registrationSortBy === 'created_at'}
                  onClick={() => handleSort('created_at')}
                >
                  Registered
                  <ArrowUpDown size={12} />
                </SortableHeader>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRegistrations.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
                    {registrationSearch || registrationStatusFilter
                      ? 'No registrations match your filters.'
                      : 'No registrations found.'}
                  </td>
                </tr>
              ) : (
                paginatedRegistrations.map((reg) => (
                  <tr key={reg.id}>
                    <CheckboxCell theme={theme}>
                      <input
                        type="checkbox"
                        checked={selectedRegistrations.has(reg.id)}
                        onChange={(e) => handleSelectRegistration(reg.id, e.target.checked)}
                      />
                    </CheckboxCell>
                    <td>
                      <div style={{ fontWeight: 600 }}>{reg.full_name}</div>
                      <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                        {reg.email}
                      </div>
                      {reg.membership_id && (
                        <div style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                          Member ID: {reg.membership_id}
                        </div>
                      )}
                    </td>
                    <td>
                      {reg.organization || '—'}
                      {reg.role_title && (
                        <div style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                          {reg.role_title}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusDot status={reg.status} theme={theme}>{reg.status}</StatusDot>
                      {reg.participation_history > 0 && (
                        <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, marginTop: '0.25rem' }}>
                          {reg.participation_history} previous
                        </div>
                      )}
                    </td>
                    <td>
                      {reg.created_at
                        ? new Date(reg.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="ghost"
                          leftIcon={<Eye size={14} />}
                          onClick={() => openRegistrationDetail(reg)}
                        >
                          View
                        </Button>
                        {reg.status !== 'approved' && reg.status !== 'completed' && (
                          <Button
                            size="small"
                            variant="primary"
                            onClick={() => handleRegistrationStatus(reg, 'approved')}
                          >
                            Approve
                          </Button>
                        )}
                        {reg.status !== 'waitlisted' && (
                          <Button
                            size="small"
                            variant="outline"
                            onClick={() => handleRegistrationStatus(reg, 'waitlisted')}
                          >
                            Waitlist
                          </Button>
                        )}
                        {reg.status !== 'declined' && (
                          <Button
                            size="small"
                            variant="danger"
                            onClick={() => handleRegistrationStatus(reg, 'declined')}
                          >
                            Decline
                          </Button>
                        )}
                        {reg.status === 'approved' && (
                          <Button
                            size="small"
                            variant="secondary"
                            leftIcon={<CheckCircle2 size={14} />}
                            onClick={() => handleRegistrationStatus(reg, 'completed')}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </RegistrationTable>
        </div>

        {totalPages > 1 && (
          <PaginationControls theme={theme}>
            <PaginationInfo theme={theme}>
              Showing {((registrationPage - 1) * registrationPageSize) + 1} to {Math.min(registrationPage * registrationPageSize, filteredRegistrations.length)} of {filteredRegistrations.length} registrations
            </PaginationInfo>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button
                size="small"
                variant="ghost"
                leftIcon={<ChevronLeft size={14} />}
                onClick={() => setRegistrationPage(p => Math.max(1, p - 1))}
                disabled={registrationPage === 1}
              >
                Previous
              </Button>
              <span style={{ fontSize: theme.typography.fontSize.sm }}>
                Page {registrationPage} of {totalPages}
              </span>
              <Button
                size="small"
                variant="ghost"
                rightIcon={<ChevronRight size={14} />}
                onClick={() => setRegistrationPage(p => Math.min(totalPages, p + 1))}
                disabled={registrationPage === totalPages}
              >
                Next
              </Button>
            </div>
          </PaginationControls>
        )}
      </Card>

      <Modal
        isOpen={showRegistrationDetailModal}
        onClose={() => setShowRegistrationDetailModal(false)}
        title={registrationDetail ? `Application • ${registrationDetail.full_name}` : 'Application Detail'}
        size="large"
      >
        {registrationDetail ? (
          <>
            <FormDetailsWrapper theme={theme} style={{ marginTop: 0 }}>
              <FormDetailsGrid>
                <FormField>
                  <FormFieldLabel theme={theme}>Applicant</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.full_name}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Email</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.email}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Phone</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.phone || '—'}</FormFieldValue>
                </FormField>
                <FormField>
                  <FormFieldLabel theme={theme}>Status</FormFieldLabel>
                  <FormFieldValue theme={theme}>{registrationDetail.status}</FormFieldValue>
                </FormField>
                {registrationDetail.membership_id && (
                  <FormField>
                    <FormFieldLabel theme={theme}>Membership ID</FormFieldLabel>
                    <FormFieldValue theme={theme}>{registrationDetail.membership_id}</FormFieldValue>
                  </FormField>
                )}
                {registrationDetail.status === 'completed' && (
                  <FormField>
                    <FormFieldLabel theme={theme}>Completion Status</FormFieldLabel>
                    <FormFieldValue theme={theme}>
                      <div style={{ 
                        padding: '0.75rem', 
                        background: '#ecfdf5', 
                        borderRadius: '6px',
                        border: '1px solid #10b981',
                        color: '#059669'
                      }}>
                        ✓ Training completed. Participant has been notified via email.
                      </div>
                    </FormFieldValue>
                  </FormField>
                )}
                <FormField>
                  <FormFieldLabel theme={theme}>Submitted On</FormFieldLabel>
                  <FormFieldValue theme={theme}>
                    {registrationDetail.created_at
                      ? new Date(registrationDetail.created_at).toLocaleString()
                      : '—'}
                  </FormFieldValue>
                </FormField>
              </FormDetailsGrid>
            </FormDetailsWrapper>

            <FormDetailsWrapper theme={theme}>
              <FormDetailsTitle theme={theme}>Form Responses</FormDetailsTitle>
              {registrationDetailResponses.length === 0 ? (
                <FormFieldValue theme={theme}>No additional responses were provided.</FormFieldValue>
              ) : (
                <FormDetailsGrid>
                  {registrationDetailResponses.map((response, index) => (
                    <FormField key={`detail-${index}`}>
                      <FormFieldLabel theme={theme}>{response.label}</FormFieldLabel>
                      <FormFieldValue theme={theme}>{response.value}</FormFieldValue>
                    </FormField>
                  ))}
                </FormDetailsGrid>
              )}
            </FormDetailsWrapper>
          </>
        ) : (
          <p>No registration selected.</p>
        )}
      </Modal>

      {/* Confirm Dialog */}
      <Modal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        title={confirmDialog.title}
        size="small"
      >
        <DialogContent>
          <DialogMessage theme={theme}>{confirmDialog.message}</DialogMessage>
          <DialogActions>
            <Button
              variant="ghost"
              onClick={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
            >
              Cancel
            </Button>
            <Button
              variant={confirmDialog.variant}
              onClick={confirmDialog.onConfirm}
            >
              {confirmDialog.confirmText}
            </Button>
          </DialogActions>
        </DialogContent>
      </Modal>

      {/* Decline Dialog with Reason */}
      <Modal
        isOpen={declineDialog.isOpen}
        onClose={() => setDeclineDialog({ isOpen: false, registration: null, reason: '' })}
        title="Decline Registration"
        size="small"
      >
        <DialogContent>
          <DialogMessage theme={theme}>
            Provide an optional reason for declining {declineDialog.registration?.full_name}'s registration:
          </DialogMessage>
          <Textarea
            theme={theme}
            value={declineDialog.reason}
            onChange={(e) => setDeclineDialog(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Enter reason for declining (optional)..."
          />
          <DialogActions>
            <Button
              variant="ghost"
              onClick={() => setDeclineDialog({ isOpen: false, registration: null, reason: '' })}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeclineConfirm}
            >
              Decline Registration
            </Button>
          </DialogActions>
        </DialogContent>
      </Modal>

      {/* Bulk Action Confirmation Dialog */}
      <Modal
        isOpen={bulkActionDialog.isOpen}
        onClose={() => setBulkActionDialog({ isOpen: false, status: '', count: 0 })}
        title="Confirm Bulk Action"
        size="small"
      >
        <DialogContent>
          <DialogMessage theme={theme}>
            Are you sure you want to update {bulkActionDialog.count} registration(s) to "{bulkActionDialog.status}"?
          </DialogMessage>
          <DialogActions>
            <Button
              variant="ghost"
              onClick={() => setBulkActionDialog({ isOpen: false, status: '', count: 0 })}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={confirmBulkAction}
            >
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </Modal>
    </PageContainer>
  );
};

export default TrainingRegistrations;


